import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import config from '../../config/config';
import { QRCodeCanvas } from "qrcode.react";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/login.css';
import ParticlesBackground from '../../components/common/ParticlesBackground';
import Modal from '../../components/common/Modal';

const DataFAPage = () => {
  const [username, setUsername] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [secretExists, setSecretExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: ''
  });

  useEffect(() => {
    const check2FAStatus = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decodedToken = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp < currentTime) {
            console.log("Token expired, redirecting to login");
            localStorage.removeItem('token');
            navigate('/', { state: { sessionExpired: true } });
            return;
          }

          if (decodedToken.twoFactorVerified) {
            let targetPath = '/';
            if (decodedToken.role === 1) {
              targetPath = '/kl';
            } else if (decodedToken.role === 0 || decodedToken.role === 2) {
              targetPath = '/admin';
            } else if (decodedToken.role === 3) {
              targetPath = '/publikator';
            }
            navigate(targetPath, { replace: true });
          }
        } catch (error) {
          console.error("Token tidak valid:", error);
          localStorage.removeItem('token');
          navigate('/');
        }
      } else {
        navigate('/');
      }
    };
    check2FAStatus();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          navigate('/');
          return;
        }

        const decodedToken = jwtDecode(storedToken);

        // Check if token is expired before making request
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem('token');
          navigate('/', { state: { sessionExpired: true } });
          return;
        }

        setUsername(decodedToken.username);

        // Logika untuk generate QR code
        const response = await axios.post(`${config.API_URL}/generate-2fa`,
          { username: decodedToken.username },
          { headers: { "Content-Type": "application/json" } }
        );
        setSecretExists(response.data.secretExists);
        if (!response.data.secretExists) {
          setQrCodeUrl(response.data.qrCodeUrl);
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/', { state: { sessionExpired: true } });
        }
      }
    };
    fetchData();
  }, [navigate]);

  const showModalError = (title, message) => {
    setModalState({ isOpen: true, type: 'error', title, message });
  };

  const hideModal = () => {
    setModalState({ isOpen: false, title: '', message: '', type: 'error' });
  };

  // Fungsi untuk kembali ke halaman login
  const handleBackToLogin = () => {
    localStorage.removeItem('token');
    navigate('/', { replace: true });
  };

  const verifyOTP = async () => {
    if (!otpInput.trim()) {
      showModalError('Input Kosong', 'Kolom Kode OTP tidak boleh kosong.');
      return;
    }

    setIsLoading(true);

    try {
      const storedToken = localStorage.getItem('token');

      // Check if token exists and is valid before making request
      if (!storedToken) {
        showModalError('Sesi Berakhir', 'Sesi Anda telah berakhir. Silakan login kembali.');
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      try {
        const decodedToken = jwtDecode(storedToken);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          showModalError('Sesi Berakhir', 'Sesi Anda telah berakhir. Silakan login kembali.');
          localStorage.removeItem('token');
          setTimeout(() => {
            navigate('/', { state: { sessionExpired: true } });
          }, 2000);
          return;
        }
      } catch (decodeError) {
        console.error("Error decoding token:", decodeError);
        localStorage.removeItem('token');
        navigate('/');
        return;
      }

      const response = await axios.post(
        `${config.API_URL}/verify-2fa`,
        { username, token: otpInput },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${storedToken}`
          }
        }
      );

      if (response.data.verified) {
        localStorage.setItem('token', response.data.token);
        const decodedToken = jwtDecode(response.data.token);
        if (response.data.message === "Session renewed with 2FA verification") {
          console.log("Session renewed successfully");
        }

        let targetPath = '/';
        if (decodedToken.role === 1) {
          targetPath = '/kl';
        } else if (decodedToken.role === 0 || decodedToken.role === 2) {
          targetPath = '/admin';
        } else if (decodedToken.role === 3) {
          targetPath = '/publikator';
        }
        navigate(targetPath);
      }

    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          if (error.response.data.requireLogin ||
            error.response.data.message.includes('expired') ||
            error.response.data.message.includes('invalid')) {
            showModalError(
              'Sesi Berakhir',
              'Sesi Anda telah berakhir. Anda akan dialihkan ke halaman login.'
            );
            localStorage.removeItem('token');
            setTimeout(() => {
              navigate('/', { state: { sessionExpired: true } });
            }, 2000);
          } else {
            showModalError('Autentikasi Gagal', error.response.data.message || 'Token tidak valid.');
          }
        } else if (error.response.status === 400) {
          showModalError('OTP Tidak Valid', 'Kode OTP yang Anda masukkan salah. Silakan coba lagi.');
        } else {
          showModalError('Error', error.response.data.message || 'Terjadi kesalahan pada server.');
        }
      } else {
        showModalError('Koneksi Bermasalah', 'Gagal terhubung ke server. Periksa koneksi internet Anda.');
      }
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add token expiration check interval
  useEffect(() => {
    const checkTokenExpiration = setInterval(() => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decodedToken = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp - currentTime < 60) {
            console.log("Token expiring soon, warning user");
            showModalError(
              'Sesi Akan Berakhir',
              'Sesi Anda akan segera berakhir. Silakan segera verifikasi OTP Anda.'
            );
            clearInterval(checkTokenExpiration);
          }
        } catch (error) {
          console.error("Error checking token expiration:", error);
        }
      }
    }, 30000);

    return () => clearInterval(checkTokenExpiration);
  }, []);

  return (
    <>
      <Modal
        isOpen={modalState.isOpen}
        onClose={hideModal}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />

      <div className="d-flex justify-content-center align-items-center vh-100 login-container" style={{ position: 'relative', overflow: 'hidden' }}>
        <ParticlesBackground />

        <div className="login-form-container">
          <div className="card p-5 shadow-lg text-center" style={{ width: '400px', zIndex: 10, position: 'relative', backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
            <button
              onClick={handleBackToLogin}
              className="btn btn-outline-danger position-absolute"
              style={{
                top: '15px',
                right: '15px',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '0',
                border: '1px solid #dc3545'
              }}
              title="Kembali ke Login"
              disabled={isLoading}
            >
              ×
            </button>
            <div className="d-flex justify-content-center">
              <img src="/logo.png" alt="Logo" className="img-fluid mb-3" style={{ width: '120px' }} />
            </div>
            <h3 className="fw-bold mb-3">Two-Factor Authentication (2FA)</h3>

            {!secretExists && qrCodeUrl && (
              <>
                <p className="mb-2">Scan QR Code ini dengan Google Authenticator:</p>
                <div className="d-flex justify-content-center mb-3">
                  <QRCodeCanvas value={qrCodeUrl} size={180} />
                </div>
              </>
            )}

            <input
              type="text"
              placeholder="Enter OTP Code"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !isLoading) { verifyOTP(); } }}
              className="form-control mb-3 text-center"
              disabled={isLoading}
              maxLength="6"
              autoComplete="off"
            />

            <button
              onClick={verifyOTP}
              className="btn btn-verify w-100 mb-3"
              disabled={isLoading}
            >
              {isLoading ? 'Memverifikasi...' : 'Verify'}
            </button>

            {/* Tombol alternatif untuk kembali ke login */}
            <button
              onClick={handleBackToLogin}
              className="btn btn-outline-secondary w-100"
              disabled={isLoading}
            >
              Kembali ke Login
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataFAPage;